import { useEffect, useCallback, useRef } from 'react'
import { FormInstance } from 'antd'
import dayjs from 'dayjs'
import { GTOB_FORM_DATA_PREFIX, DATE_INPUT_KEYS } from '../constants'
import Token from '../token'

interface UseFormPersistenceOptions {
  formKey: string
  ttl?: number
  isFormTouched: boolean
  onRestore?: () => void
}

interface StoredFormData {
  data: Record<string, unknown>
  timestamp: number
  ttl: number
}

const DEFAULT_TTL = 60 * 60 * 1000 // 1 hour

/**
 * Converts date strings back to dayjs objects for date fields
 */
const restoreDateFields = (data: Record<string, unknown>): Record<string, unknown> => {
  const restored = { ...data }

  DATE_INPUT_KEYS.forEach((key) => {
    if (restored[key] && typeof restored[key] === 'string') {
      restored[key] = dayjs(restored[key] as string)
    }
  })

  if (restored.datetime && typeof restored.datetime === 'string') {
    restored.datetime = dayjs(restored.datetime as string)
  }

  if (Array.isArray(restored.people)) {
    restored.people = restored.people.map((person: Record<string, unknown>) => {
      const restoredPerson = { ...person }
      DATE_INPUT_KEYS.forEach((key) => {
        if (restoredPerson[key] && typeof restoredPerson[key] === 'string') {
          restoredPerson[key] = dayjs(restoredPerson[key] as string)
        }
      })
      return restoredPerson
    })
  }

  return restored
}

/**
 * Hook to persist form data to localStorage and restore it after authentication.
 */
export default function useFormPersistence<T extends Record<string, unknown>>(
  form: FormInstance<T>,
  options: UseFormPersistenceOptions
) {
  const { formKey, ttl = DEFAULT_TTL, isFormTouched, onRestore } = options
  const storageKey = `${GTOB_FORM_DATA_PREFIX}${formKey}`
  const hasRestoredRef = useRef(false)

  /**
   * Save the current form data to localStorage
   */
  const saveFormData = useCallback(() => {
    if (!isFormTouched) return

    const formData = form.getFieldsValue(true)
    const storedData: StoredFormData = {
      data: formData as Record<string, unknown>,
      timestamp: Date.now(),
      ttl,
    }
    localStorage.setItem(storageKey, JSON.stringify(storedData))
  }, [form, isFormTouched, storageKey, ttl])

  /**
   * Clear the stored form data
   */
  const clearFormData = useCallback(() => {
    localStorage.removeItem(storageKey)
  }, [storageKey])

  /**
   * Restore form data from localStorage if it exists and is still valid
   */
  const restoreFormData = useCallback(() => {
    if (hasRestoredRef.current) return false

    const storedDataStr = localStorage.getItem(storageKey)
    if (!storedDataStr) return false

    try {
      const storedData: StoredFormData = JSON.parse(storedDataStr)
      const isExpired = Date.now() - storedData.timestamp > storedData.ttl

      if (isExpired) {
        clearFormData()
        return false
      }

      const restoredData = restoreDateFields(storedData.data)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setFieldsValue(restoredData as any)
      hasRestoredRef.current = true

      clearFormData()

      onRestore?.()
      return true
    } catch {
      clearFormData()
      return false
    }
  }, [form, storageKey, clearFormData, onRestore])

  /**
   * Proactively check and refresh token to prevent session expiry during form submission
   */
  const ensureTokenBeforeSubmit = useCallback(async (): Promise<boolean> => {
    saveFormData()

    const isValid = await Token.ensureValidToken()

    if (isValid) {
      return true
    }

    return false
  }, [saveFormData])

  useEffect(() => {
    if (!isFormTouched) return

    const interval = setInterval(saveFormData, 30000)

    return () => clearInterval(interval) // eslint-disable-line consistent-return
  }, [isFormTouched, saveFormData])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isFormTouched) {
        saveFormData()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isFormTouched, saveFormData])

  useEffect(() => {
    restoreFormData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    saveFormData,
    clearFormData,
    restoreFormData,
    ensureTokenBeforeSubmit,
  }
}
